import React, { useEffect, useState } from "react";
import { Container, Form } from "react-bootstrap";
import { Helmet } from "react-helmet";
import axios from "axios";
import Select from "react-select";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";

const data = {
  propertyTypes: [
    "Single family",
    "Ranch",
    "2-story",
    "Mobile home owned land",
    "Mobile home rented land",
    "Multifamily",
    "Bungalow",
    "Cottage",
    "Townhouse",
    "Condominium",
    "Duplex",
    "Farmhouse",
    "Split-level home",
    "Land",
    "Commercial Property",
  ],
};

const highlightColor = "#6abc45";

// Nationwide options
const nationwideStateOption = { label: "Nationwide", value: "nationwide" };
const nationwideCityOption = { label: "Nationwide", value: "nationwide" };

const AutoLeads = ({ autoPurchaseSettings }) => {
  const [uid, setUid] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [loadingStates, setLoadingStates] = useState(true);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const [priceRange, setPriceRange] = useState([500, 1500]);
  const [message, setMessage] = useState("");
  const [limit, setLimit] = useState("");
  const [isEnabled, setIsEnabled] = useState(
    autoPurchaseSettings.enabled || false
  );
  const [isInitializing, setIsInitializing] = useState(true);
  const navigate = useNavigate();
  const auth = getAuth();

  const apiKey = process.env.REACT_APP_API_KEY;
  useEffect(() => {
    if (autoPurchaseSettings && isInitializing) {
      const states =
        autoPurchaseSettings.states?.map((state) => ({
          label: state,
          value: state,
        })) || [];
      setSelectedStates(states);

      const cities =
        autoPurchaseSettings.cities?.map((city) => ({
          label: city,
          value: city,
        })) || [];
      setSelectedCities(cities);

      const types =
        autoPurchaseSettings.propertyTypes?.map((type) => ({
          label: type,
          value: type,
        })) || [];
      setSelectedTypes(types);

      if (autoPurchaseSettings.leadPriceRange) {
        setPriceRange([
          autoPurchaseSettings.leadPriceRange.min,
          autoPurchaseSettings.leadPriceRange.max,
        ]);
      }

      setLimit(autoPurchaseSettings.dailyLimit ?? "");
      setIsEnabled(autoPurchaseSettings.enabled ?? false);

      setIsInitializing(false);
    }
  }, [autoPurchaseSettings]);

  useEffect(() => {
    const fetchCitiesForStates = async () => {
      if (isInitializing) return;
    };
    fetchCitiesForStates();
  }, [selectedStates, isInitializing]);



  useEffect(() => {
    const fetchStates = async () => {
      const cachedStates = localStorage.getItem("cached_states");
      if (cachedStates) {
        setStateOptions([nationwideStateOption, ...JSON.parse(cachedStates)]);
        setLoadingStates(false);
        return;
      }

      try {
        const resp = await axios.post(
          "https://countriesnow.space/api/v0.1/countries/states",
          { country: "United States" }
        );
        const opts = resp.data.data.states.map((s) => ({
          label: s.name,
          value: s.name,
        }));
        setStateOptions([nationwideStateOption, ...opts]);
        localStorage.setItem("cached_states", JSON.stringify(opts));
      } catch (err) {
        console.error("Error fetching states:", err);
        Swal.fire({
          icon: "error",
          title: "Oops…",
          text: "Failed to load states. Try again later.",
        });
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    const fetchCitiesForStates = async () => {
      if (selectedStates.some((s) => s.value === "nationwide")) {
        setCityOptions([nationwideCityOption]);
        setSelectedCities([nationwideCityOption]);
        return;
      }

      if (!selectedStates.length) {
        setCityOptions([]);
        setSelectedCities([]);
        return;
      }

      try {
        const allCities = [];

        for (const opt of selectedStates) {
          const cacheKey = `cities_${opt.value}`;
          const cachedCities = localStorage.getItem(cacheKey);

          let cities;
          if (cachedCities) {
            cities = JSON.parse(cachedCities);
          } else {
            const response = await axios.post(
              "https://countriesnow.space/api/v0.1/countries/state/cities",
              {
                country: "United States",
                state: opt.value,
              }
            );
            cities = response.data.data || [];
            localStorage.setItem(cacheKey, JSON.stringify(cities));
          }

          allCities.push(...cities);
        }

        const uniqueSortedCities = [...new Set(allCities)].sort((a, b) =>
          a.localeCompare(b)
        );
        const cityOpts = uniqueSortedCities.map((c) => ({
          label: c,
          value: c,
        }));

        setCityOptions(cityOpts);
        const cities =
        autoPurchaseSettings.cities?.map((city) => ({
          label: city,
          value: city,
        })) || [];
        setSelectedCities(cities ||[]);
      } catch (err) {
        console.error("Error fetching cities:", err);
        Swal.fire({
          icon: "error",
          title: "Oops…",
          text: "Failed to load cities. Try again later.",
        });
        setCityOptions([]);
        setSelectedCities([]);
      }
    };

    fetchCitiesForStates();
  }, [selectedStates]);

  const propertyTypeOptions = data.propertyTypes.map((t) => ({
    label: t,
    value: t,
  }));

  const handleStateChange = (selectedOptions) => {
    if (
      selectedOptions &&
      selectedOptions.some((opt) => opt.value === "nationwide")
    ) {
      setSelectedStates([nationwideStateOption]);
    } else {
      setSelectedStates(selectedOptions || []);
    }
  };

  const handleCityChange = (selectedOptions) => {
    if (
      selectedOptions &&
      selectedOptions.some((opt) => opt.value === "nationwide")
    ) {
      setSelectedCities([nationwideCityOption]);
    } else {
      setSelectedCities(selectedOptions || []);
    }
  };

  const handleSubmit = async () => {
    if (!selectedStates.length) {
      setMessage("Please select at least one state.");
      return;
    }

    const isNationwideState = selectedStates.some(
      (s) => s.value === "nationwide"
    );

    if (
      isNationwideState &&
      !selectedCities.some((c) => c.value === "nationwide")
    ) {
      setMessage(
        "Please select Nationwide for cities when Nationwide is selected for states."
      );
      return;
    }

    if (!selectedTypes.length) {
      setMessage("Please select at least one property type.");
      return;
    }

    let citiesToSend = [];
    if (isNationwideState) {
      citiesToSend = ["nationwide"];
    } else {
      if (selectedCities.length === 0) {
        citiesToSend = cityOptions.map((city) => city.value);
      } else {
        citiesToSend = selectedCities.map((city) => city.value);
      }
    }

    const payload = {
      userId: uid,
      enabled: isEnabled,
      states: selectedStates.map((s) => s.value),
      cities: citiesToSend,
      propertyTypes: selectedTypes.map((t) => t.value),
      leadPriceRange: {
        min: priceRange[0],
        max: priceRange[1],
      },
      dailyLimit: limit,
    };

    try {
      await axios.post(
        "https://api.v1.spotyourleads.com/user/set-auto-purchase",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": apiKey,
          },
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Filter submitted successfully",
      });
      setMessage("");
    } catch (error) {
      console.error("Error sending data:", error);
      Swal.fire({
        icon: "error",
        title: "Oops…",
        text: "Error sending the data",
      });
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUid(user.uid);
      } else {
        setUid(null);
        navigate("/sign-in");
      }
      setAuthLoaded(true);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  if (!authLoaded || loadingStates) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  const sendDataToAPI = async (enabled) => {
    const payload = {
      userId: uid,
      enabled: enabled,
    };

    try {
      await axios.post(
        "https://api.v1.spotyourleads.com/user/set-auto-purchase",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": apiKey,
          },
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Auto Purchase disabled Successfully",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops…",
        text: error,
      });
    }
  };

  const handleToggle = () => {
    const newValue = !isEnabled;
    setIsEnabled(newValue);

    if (newValue === false) {
      sendDataToAPI(newValue);
    }
  };


  return (
    <section className="auto-leads">

      <div className="about">
        <Container>
          <div className="pricing-title">
            <h2>Auto Purchase</h2>
          </div>
        </Container>
      </div>

      <Container>
        <div className="max-w-md mx-auto p-8 space-y-8">
          <div className="d-flex a align-items-center justify-content-between">
            <h3 className="text-xl font-semibold text-gray-800">
              Enable Auto Purchase
            </h3>
            <Form.Check
              type="switch"
              id="auto-purchase-switch"
              checked={isEnabled}
              className="custom-switch"
              onChange={handleToggle}
            />
          </div>

          {isEnabled && (
            <>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  Select State(s)
                </h3>
                <Select
                  options={stateOptions}
                  value={selectedStates}
                  isMulti
                  onChange={handleStateChange}
                  placeholder="Choose one or more states..."
                  classNamePrefix="react-select input-lead"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Select "Nationwide" to cover all states
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  Select City(s)
                </h3>
                <Select
                  options={cityOptions}
                  value={selectedCities}
                  isMulti
                  isDisabled={selectedStates.some(
                    (s) => s.value === "nationwide"
                  )}
                  onChange={handleCityChange}
                  placeholder={
                    selectedStates.some((s) => s.value === "nationwide")
                      ? "Nationwide selected"
                      : selectedStates.length
                      ? "Choose cities or leave empty for all"
                      : "Select state(s) first"
                  }
                  classNamePrefix="react-select input-lead"
                />
                {!selectedStates.some((s) => s.value === "nationwide") &&
                  selectedStates.length > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      Leave empty to select all cities in the selected states
                    </p>
                  )}
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  Select Property Type(s)
                </h3>
                <Select
                  options={propertyTypeOptions}
                  value={selectedTypes}
                  isMulti
                  onChange={(opts) => setSelectedTypes(opts || [])}
                  placeholder="Choose property type(s)..."
                  classNamePrefix="react-select input-lead"
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  Select Price Range
                </h3>
                <div className="flex flex-col gap-4">
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    step="50"
                    value={priceRange[0]}
                    onChange={(e) =>
                      setPriceRange([+e.target.value, priceRange[1]])
                    }
                    className="w-full accent-green-500"
                  />
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    step="50"
                    value={priceRange[1]}
                    onChange={(e) =>
                      setPriceRange([priceRange[0], +e.target.value])
                    }
                    className="w-full accent-green-500 input-lead"
                  />
                  <div className="text-sm text-gray-600">
                    From <span className="font-semibold">${priceRange[0]}</span>{" "}
                    to <span className="font-semibold">${priceRange[1]}</span>{" "}
                    dollars
                  </div>
                </div>
              </div>

              <div className="max-w-md mx-auto p-8 space-y-8">
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  Set Daily Limit
                </h3>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  placeholder="Enter daily limit (1-100)"
                  className="w-full border border-gray-300 input-lead rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="text-center">
                <button
                  onClick={handleSubmit}
                  className="btn-submit px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                >
                  Submit
                </button>

                {message && (
                  <p className="text-center text-sm font-medium text-red-600 mt-2">
                    {message}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </Container>
    </section>
  );
};

export default AutoLeads;
